import React, { Dispatch, Fragment, SetStateAction } from 'react';

import Chevron from '@/assets/chevron.svg?react';
import Cross from '@/assets/cross.svg?react';
import { ScrollArea } from '@/components/ui/Select/ScrollArea';
import cn from '@/utils/cn';

import { Listbox, Transition } from '@headlessui/react';
import { Float } from '@headlessui-float/react';
import { Trans, useLingui } from '@lingui/react';

export type MultiSelectItem = {
  value: string;
  label: string;
  group?: string;
};
export interface MultiSelectProps {
  id?: string;
  data: MultiSelectItem[];
  error?: string;
  success?: boolean;
  placeholder?: string;
  className?: string;
  value: MultiSelectItem[];
  onChange: Dispatch<SetStateAction<MultiSelectItem[]>>;
  ['data-testid']?: string;
}

const MultiSelect = React.forwardRef<
  React.ElementRef<typeof Listbox>,
  MultiSelectProps
>(
  (
    {
      data,
      error,
      success,
      placeholder,
      className,
      id,
      value = [],
      onChange,
      'data-testid': testId,
    },
    ref
  ) => {
    const removeItem = (removedItem: string) => {
      onChange(value.filter((item) => item?.value !== removedItem));
    };
    const { i18n } = useLingui();

    const otherGroup = i18n.t('Inne');

    const isGrouped = data.some((item) => item.group);

    if (isGrouped && data.some((item) => !item.group)) {
      data = data
        .map((item) => {
          return {
            ...item,
            group: item.group ?? otherGroup,
          };
        })
        .sort((a, b) => {
          if (a.group === otherGroup) return 1;
          if (b.group === otherGroup) return -1;
          return 0;
        });
    }

    const groups = [...new Set(data.map((item) => item.group || otherGroup))];

    const getDataByGroup = (arr: MultiSelectItem[], group: string) => {
      return arr.filter((item) => item.group === group);
    };

    const groupedData = groups.reduce((acc, group) => {
      return {
        ...acc,
        [group]: getDataByGroup(data, group),
      };
    }, {});

    return (
      <div className={className} id={id}>
        <Listbox value={value} onChange={onChange} ref={ref} multiple>
          {({ open }) => (
            <div className="relative">
              <div
                className={cn(
                  'pointer-events-none absolute right-0 top-1/2 z-10 flex -translate-y-1/2 cursor-pointer items-center gap-2.5 pr-4	'
                )}
              >
                <div className="h-6 w-[1px] bg-dark-gray-300" />
                <Listbox.Button
                  className={cn(
                    (className = 'pointer-events-none text-gray-200')
                  )}
                >
                  <Chevron
                    width={14}
                    className={cn(
                      'shrink-0 rotate-90 fill-[currentColor] text-dark-gray-300 transition-transform duration-300 ease-in-out',
                      open && 'rotate-[270deg]'
                    )}
                  />
                </Listbox.Button>
              </div>
              <div className="absolute right-[41px] top-1/2 z-10 flex -translate-y-1/2 cursor-pointer items-center gap-2.5	">
                {value.length > 0 && (
                  <button
                    className={cn('p-2.5')}
                    onClick={() => onChange([])}
                    data-testid="clear-all"
                  >
                    <Cross width={14} className="text-dark-gray-300" />
                  </button>
                )}
              </div>
              <Float
                as="div"
                className="relative"
                placement="bottom"
                offset={4}
                portal
                adaptiveWidth
              >
                <Listbox.Button
                  className={cn(
                    'body flex min-h-[52px] w-full flex-wrap items-center gap-1.5 rounded-md border border-dark-gray-100 bg-gray-100 px-3.5 py-[9px] pr-[72px] text-dark-gray-500 transition-colors duration-200 ease-in-out peer-hover:border-blue-600 hover:border-blue-600 focus:border-blue-600 focus:outline-none active:border-blue-600',
                    'focus:ring-4 focus:ring-primary/20',
                    success &&
                      'border-green-400 hover:border-green-400 focus:border-green-400',
                    open && 'border-blue-600',
                    error && 'border-red-400'
                  )}
                  data-testid={testId}
                >
                  {value.length > 0 ? (
                    value.map(({ value, label }) => (
                      <div
                        onClick={(event) => event.stopPropagation()}
                        className="flex w-fit cursor-default items-stretch gap-2 rounded bg-gray-200 text-left"
                        key={label}
                      >
                        <p className="caption-large my-1.5 ml-2.5"> {label}</p>
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label="Remove item"
                          onClick={() => removeItem(value)}
                          className="flex cursor-pointer items-center rounded px-1 hover:bg-gray-500"
                        >
                          <Cross width={14} className="text-dark-gray-500" />
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-dark-gray-200">
                      {placeholder ?? <Trans id="Wybierz">Wybierz</Trans>}
                    </p>
                  )}
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  enter="transition-all duration-100 ease-in-out "
                >
                  {isGrouped ? (
                    <Listbox.Options className="absolute top-full z-10 mt-0.5 max-h-[266px] w-full max-w-[670px] space-y-1 overflow-auto rounded-md bg-white p-1.5 text-base shadow-shadow14 ring-1 ring-black/5 scrollbar scrollbar-track-gray-400 scrollbar-thumb-blue-600 focus:outline-none ">
                      <ScrollArea mah={'max-h-[250px]'}>
                        <div
                          className={cn(
                            'flex flex-col gap-1 border-gray-400',
                            data.length > 7 && 'pr-3.5'
                          )}
                        >
                          {groups.map((group) => (
                            <div key={group}>
                              {(
                                groupedData[
                                  (group ||
                                    otherGroup) as keyof typeof groupedData
                                ] as MultiSelectItem[]
                              ).length !==
                                value.filter((v) => v.group === group)
                                  .length && (
                                <Listbox.Option
                                  value={null}
                                  className={() =>
                                    cn(
                                      'caption-large-semi pointer-events-none relative select-none rounded px-2 py-1 uppercase text-oxford-blue-500 transition-colors duration-200 hover:bg-gray-200'
                                    )
                                  }
                                >
                                  {group}
                                </Listbox.Option>
                              )}
                              {value.length < data.length ? (
                                (
                                  groupedData[
                                    (group ||
                                      otherGroup) as keyof typeof groupedData
                                  ] as MultiSelectItem[]
                                )
                                  .filter(
                                    (item) =>
                                      !value.some((v) => v.value === item.value)
                                  )
                                  .map((item) => (
                                    <Listbox.Option
                                      key={
                                        item.value || item.label || item.group
                                      }
                                      value={item}
                                      className={({ selected, active }) =>
                                        cn(
                                          'relative cursor-pointer select-none rounded py-1 pl-3 pr-2 text-oxford-blue-500 transition-colors duration-200 hover:bg-gray-200',
                                          selected && 'hidden',
                                          active && 'bg-gray-200'
                                        )
                                      }
                                      data-testid={`${testId}-${item.value}`}
                                    >
                                      {item.label.includes(': ')
                                        ? item.label.split(': ').pop()
                                        : item.label}
                                    </Listbox.Option>
                                  ))
                              ) : (
                                <div className="flex w-full justify-center py-1 text-base leading-6 text-dark-gray-200">
                                  <Trans id="Brak opcji">Brak opcji</Trans>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </Listbox.Options>
                  ) : (
                    <Listbox.Options className="absolute top-full z-10 mt-0.5 max-h-[266px] w-full max-w-[670px] space-y-1 overflow-auto rounded-md bg-white p-1.5 text-base shadow-shadow14 ring-1 ring-black/5 scrollbar scrollbar-track-gray-400 scrollbar-thumb-blue-600 focus:outline-none">
                      <ScrollArea mah={'max-h-[250px]'}>
                        <div
                          className={cn(
                            'flex flex-col gap-1 border-gray-400',
                            data.length > 7 && 'pr-3.5'
                          )}
                        >
                          {value.length < data.length ? (
                            data
                              .filter(
                                (item) =>
                                  !value.some((v) => v.value === item.value)
                              )
                              .map((item) => (
                                <Listbox.Option
                                  key={item.value}
                                  value={item}
                                  data-testid={`${testId}-${item.value}`}
                                  className={({ selected, active }) =>
                                    cn(
                                      'relative cursor-pointer select-none rounded px-2 py-1 text-oxford-blue-500 transition-colors duration-200 hover:bg-gray-200',
                                      selected && 'hidden',
                                      active && 'bg-gray-200'
                                    )
                                  }
                                >
                                  {item.label}
                                </Listbox.Option>
                              ))
                          ) : (
                            <div
                              className="flex w-full justify-center py-1 text-base leading-6 text-dark-gray-200"
                              data-testid={`${testId}-no-option`}
                            >
                              <Trans id="Brak opcji">Brak opcji</Trans>
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </Listbox.Options>
                  )}
                </Transition>
              </Float>
            </div>
          )}
        </Listbox>
      </div>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

export default MultiSelect;
